// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useCallback, useEffect, useState } from 'react';
import { Box, Tab, Typography } from '@mui/material';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseTabList, Loading } from '~/components/common';
import {
  JobActivitiesTemplate,
  JobDocumentsTemplate,
  JobEstimateTemplate,
  JobInvoiceTemplate,
  JobLineItemsTemplate,
  JobNoteTemplate,
  JobPOTemplate,
  JobScheduleTemplate,
  JobSummaryTemplate,
  JobTaskTemplate,
} from '~/pages/template';
// Others
import { DEFAULT_NUMBER_ONE, EMPTY_STRING } from '~/utils/constants/common';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { IBaseTabItem } from '~/utils/interface/common';
import { JobDetailTabsEnum, RoleEnum, TypeParamJobDetail } from '~/utils/enum/common';
import { getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getJobDetail } from '~/thunks/job/jobThunk';
import { IJobDetail, IParamsDetailJob } from '~/utils/interface/job';
import { StorageEnum } from '~/utils/enum/base';
import { staffRouteAbsolute } from '~/utils/routes/staff';
// Styles, images, icons
import styles from './JobDetail.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const JobDetail = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/job', 'common/base']);
  const { hash } = useLocation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: jobId } = useParams();

  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [
      { label: JobDetailTabsEnum.SUMMARY, component: <JobSummaryTemplate /> },
    ];
    items.push({ label: JobDetailTabsEnum.PO, component: <JobPOTemplate /> });

    items.push({ label: JobDetailTabsEnum.LINE_ITEMS, component: <JobLineItemsTemplate /> });

    items.push({ label: JobDetailTabsEnum.TASK, component: <JobTaskTemplate /> });

    items.push({ label: JobDetailTabsEnum.ESTIMATE, component: <JobEstimateTemplate /> });

    items.push({ label: JobDetailTabsEnum.INVOICE, component: <JobInvoiceTemplate /> });

    items.push({ label: JobDetailTabsEnum.DOCUMENTS, component: <JobDocumentsTemplate /> });

    items.push({
      label: JobDetailTabsEnum.SCHEDULE,
      component: <JobScheduleTemplate />,
    });

    items.push({ label: JobDetailTabsEnum.NOTE, component: <JobNoteTemplate /> });

    items.push({ label: JobDetailTabsEnum.ACTIVITIES, component: <JobActivitiesTemplate /> });

    setTabItems(items);

    if (!jobId) return;
    const params: IParamsDetailJob = {
      jobId,
      type: TypeParamJobDetail.SUMMARY,
    };

    handleGetJobDetail(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(JobDetailTabsEnum.SUMMARY);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [jobDetail, setJobDetail] = useState<IJobDetail>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(DEFAULT_NUMBER_ONE));
  }, [hash]);

  useEffect(() => {
    getMenuItems();
  }, [getMenuItems]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBackToJobs = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.jobs);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.jobs);
        break;
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate(`#${newValue}`);
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleNavigateToCustomerDetail = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(`${adminRouteAbsolute.customers}/${jobDetail?.customer?.id}`);
        break;

      case RoleEnum.STAFF:
        navigate(`${staffRouteAbsolute.customers}/${jobDetail?.customer?.id}`);
        break;
    }
  };

  const handleGetJobDetail = (params: IParamsDetailJob) => {
    setIsLoading(true);

    dispatch(getJobDetail(params))
      .unwrap()
      .then((res) => {
        const { data } = res;
        if (!data) return;

        setJobDetail(data as IJobDetail);
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='jobDetailPage'
      title={t('job_detail_title')}
      onBackToPreviousPage={handleBackToJobs}
      childrenToolbar={
        <Box component={'div'} className={cx('customerName')}>
          <Typography component={'p'} className={cx('labelName')}>
            {t('job_detail_customer_label')}
          </Typography>
          <Typography component={'p'} className={cx('valueName')} onClick={handleNavigateToCustomerDetail}>
            {getFullName({
              firstName: jobDetail?.customer?.firstName,
              lastName: jobDetail?.customer?.lastName,
            })}
          </Typography>
        </Box>
      }
    >
      <>
        <BaseTabList value={tabKey} onChange={handleTabChange}>
          {tabItems.map((item, index) => (
            <Tab
              value={item.label}
              key={index}
              label={t(`tab_label_${item.label}`)}
              className={cx('tabItem')}
            />
          ))}
        </BaseTabList>

        <Box component={'div'} className={cx('tabContent')}>
          {renderTabContent()}
        </Box>

        {isLoading && <Loading />}
      </>
    </ChildrenPage>
  );
};

export default JobDetail;
